import type { Graphics } from 'pixi.js';
import { lerpColor } from '../../color';
import type { Body, Kit, WorldDress, WorldFx } from '../../world';

const TAU = Math.PI * 2;
const FACETS = 6;
const TEETH = 14;
const RUNES = 6;
const BLOOM = 6;

function rimPoint(body: Body, a: number, k: number): readonly [number, number] {
  return [body.x + Math.cos(a) * body.radius * k * body.scaleX, body.y + Math.sin(a) * body.radius * k * body.scaleY];
}

function shard(g: Graphics, x: number, y: number, a: number, length: number, width: number): void {
  const ux = Math.cos(a);
  const uy = Math.sin(a);
  g.poly(
    [
      x + ux * length,
      y + uy * length,
      x - uy * width + ux * length * 0.25,
      y + ux * width + uy * length * 0.25,
      x - ux * length * 0.45,
      y - uy * length * 0.45,
      x + uy * width + ux * length * 0.25,
      y - ux * width + uy * length * 0.25,
    ],
    true,
  );
}

function gemFacets(k: Kit, body: Body, color: number, alpha: number): void {
  const { g, pal } = k;
  const turn = body.rotation - Math.PI / 2;
  const [cx, cy] = [body.x, body.y];
  const top0 = rimPoint(body, turn - Math.PI / FACETS, 0.96);
  const top1 = rimPoint(body, turn + Math.PI / FACETS, 0.96);
  g.poly([cx, cy, top0[0], top0[1], top1[0], top1[1]], true);
  g.fill({ color: lerpColor(color, pal.rim, 0.65), alpha: 0.42 * alpha });
  const low0 = rimPoint(body, turn + Math.PI - Math.PI / FACETS, 0.96);
  const low1 = rimPoint(body, turn + Math.PI + Math.PI / FACETS, 0.96);
  g.poly([cx, cy, low0[0], low0[1], low1[0], low1[1]], true);
  g.fill({ color: lerpColor(color, pal.ink, 0.35), alpha: 0.22 * alpha });
  for (let i = 0; i < FACETS; i++) {
    const [x, y] = rimPoint(body, turn + Math.PI / FACETS + (i / FACETS) * TAU, 0.97);
    g.moveTo(cx, cy).lineTo(x, y);
  }
  g.stroke({ width: Math.max(1, body.radius * 0.03), color: lerpColor(color, pal.ink, 0.35), alpha: 0.3 * alpha, cap: 'round' });
}

export const dress: WorldDress = {
  incomingCircle(k, body, color, alpha) {
    gemFacets(k, body, color, alpha);
    k.rimLight(body, 0.55 * alpha);
  },

  mineCircle(k, body, alpha, width, fill) {
    const { g, pal } = k;
    k.trace(body);
    g.fill({ color: fill, alpha });
    const spin = body.rotation;
    for (let i = 0; i < TEETH; i++) {
      const a = spin + (i / TEETH) * TAU;
      const half = (Math.PI / TEETH) * 0.92;
      const depth = i % 2 === 0 ? 0.52 : 0.64;
      const [x0, y0] = rimPoint(body, a - half, 0.96);
      const [xm, ym] = rimPoint(body, a, 0.96);
      const [x1, y1] = rimPoint(body, a + half, 0.96);
      const [xt, yt] = rimPoint(body, a, depth);
      g.poly([x0, y0, xm, ym, xt, yt], true).fill({ color: pal.heldLight, alpha: 0.42 * alpha });
      g.poly([xm, ym, x1, y1, xt, yt], true).fill({ color: pal.heldShade, alpha: 0.24 * alpha });
    }
    k.trace(body, 0, 0, 0.3);
    g.fill({ color: lerpColor(fill, pal.heldLight, 0.5), alpha: 0.35 * alpha });
    k.rimLight(body, 0.55 * alpha);
    k.trace(body);
    g.stroke({ width: width * 0.8, color: k.outline, alpha, join: 'round' });
  },

  target(k, body, alpha, _spread, reach, spin, edge) {
    const { g, pal } = k;
    k.trace(body);
    g.fill({ color: lerpColor(pal.bg, pal.ink, 0.5), alpha: 0.5 * alpha });
    k.trace(body);
    g.fill({ color: pal.spot, alpha: 0.07 * alpha });
    g.circle(body.x, body.y, reach).stroke({ width: 1.2, color: pal.spot, alpha: 0.45 * alpha });
    const s = Math.max(4, k.unit * 0.026);
    k.ringDots(body, reach, RUNES, spin, (x, y, _i, a) => {
      shard(g, x, y, a, s, s * 0.42);
    });
    g.fill({ color: pal.spot, alpha: 0.8 * alpha });
    k.ringDots(body, reach, RUNES, spin, (x, y, _i, a) => {
      g.moveTo(x - Math.cos(a) * s * 0.45, y - Math.sin(a) * s * 0.45).lineTo(x + Math.cos(a) * s, y + Math.sin(a) * s);
    });
    g.stroke({ width: 1, color: lerpColor(pal.spot, pal.ink, 0.5), alpha: 0.6 * alpha });
    k.trace(body);
    g.stroke({ width: edge, color: pal.rim, alpha: 0.85 * alpha, join: 'round' });
  },

  pip(k, x, y, radius, on, last, alpha, width) {
    const { g, pal } = k;
    const r = radius * 1.25;
    g.circle(x, y, r);
    if (on) g.fill({ color: last ? pal.spot : pal.incoming.circle, alpha });
    g.stroke({ width: width * 0.8, color: pal.rim, alpha: (on ? 0.9 : 0.45) * alpha });
    if (!on) return;
    g.ellipse(x - r * 0.32, y - r * 0.36, r * 0.32, r * 0.2).fill({ color: pal.rim, alpha: 0.75 * alpha });
  },

  path(k, points, count, alpha) {
    const { g, pal } = k;
    const step = Math.max(7, k.unit * 0.04);
    const size = Math.max(2.4, k.unit * 0.008);
    for (let i = 2; i < count; i += 2) {
      const x0 = points[i - 2]!;
      const y0 = points[i - 1]!;
      const dx = points[i]! - x0;
      const dy = points[i + 1]! - y0;
      const a = Math.atan2(dy, dx);
      const n = Math.min(60, Math.floor(Math.hypot(dx, dy) / step));
      for (let s = 1; s < n; s++) shard(g, x0 + (dx * s) / n, y0 + (dy * s) / n, a, size, size * 0.5);
    }
    g.fill({ color: pal.path, alpha: alpha * 2.4 });
  },
};

export const fx: WorldFx = {
  bit(g, x, y, size, i, color, alpha, rot) {
    const long = size * (1.5 + (i % 3) * 0.2);
    const ux = Math.cos(rot);
    const uy = Math.sin(rot);
    const w = size * 0.55;
    g.poly([x + ux * long, y + uy * long, x - ux * size * 0.5 - uy * w, y - uy * size * 0.5 + ux * w, x - ux * size * 0.3 + uy * w, y - uy * size * 0.3 - ux * w], true);
    g.fill({ color, alpha });
  },

  milestone(g, m, pal) {
    const turn = m.grow * 0.5;
    const ring: number[] = [];
    for (let i = 0; i < 6; i++) {
      const a = turn + (i / 6) * TAU;
      ring.push(m.x + Math.cos(a) * m.r, m.y + Math.sin(a) * m.r);
    }
    g.poly(ring, true).stroke({ width: m.width, color: pal.spot, alpha: m.fade * 0.55, join: 'round' });
    const long = m.unit * 0.09 * m.grow;
    if (long <= 0.5) return;
    for (let i = 0; i < BLOOM; i++) {
      const a = turn + (i / BLOOM) * TAU;
      shard(g, m.x + Math.cos(a) * (m.r + long * 0.45), m.y + Math.sin(a) * (m.r + long * 0.45), a, long, long * 0.32);
      g.fill({ color: pal.sparks[i % pal.sparks.length] ?? pal.held, alpha: m.fade * 0.6 });
    }
  },

  trail(g, x, y, size, angle) {
    g.star(x, y, 4, size * 1.4, size * 0.24, angle > Math.PI ? Math.PI / 4 : 0);
  },
};
